import {
  EnvironmentInjector,
  Injectable,
  inject,
  runInInjectionContext,
} from '@angular/core';
import { Store } from '@ngxs/store';
import { GanttDateToPositionPxPipe } from '@wilson/shift-timeline/assignments-gantt-chart';
import {
  DragAndDropModeService,
  getDateFromPixelPosition,
} from '@wilson/shift-timeline/services';
import {
  ShiftTimelineDataState,
  ShiftTimelineState,
} from '@wilson/shift-timeline/state';
import { addMilliseconds, startOfMinute } from 'date-fns';

@Injectable({
  providedIn: 'root',
})
export class DragAndDropHelperService {
  private readonly store = inject(Store);

  private environmentInjector = inject(EnvironmentInjector);

  calculateTimeFromPx(px: number, snappingStepSize: number): Date {
    const timelineFrameWidth = this.store.selectSnapshot(
      ShiftTimelineState.frameLength,
    );
    const daysRangeCount = this.store.selectSnapshot(
      ShiftTimelineDataState.daysRangeCount,
    );
    const totalMinutes = daysRangeCount * 24 * 60;
    const totalSnappingSlots = totalMinutes / snappingStepSize;
    const slotWidth = timelineFrameWidth / totalSnappingSlots;
    const startTime = this.store.selectSnapshot(
      ShiftTimelineDataState.timeframe,
    ).start;

    const droppedSlotIndex = Math.round(px / slotWidth);

    const time = new Date(startTime);
    time.setMinutes(time.getMinutes() + droppedSlotIndex * snappingStepSize);
    return time;
  }

  // TODO: use getDateFromPixelPosition to calculate the px from time
  calculatePxFromTime({
    time,
    snappingStepSize,
    skipShadowShiftAllowance = false,
  }: {
    time: string;
    snappingStepSize: number;
    skipShadowShiftAllowance?: boolean;
  }): number {
    const timelineFrameWidth = this.store.selectSnapshot(
      ShiftTimelineState.frameLength,
    );
    const daysRangeCount = this.store.selectSnapshot(
      ShiftTimelineDataState.daysRangeCount,
    );
    const totalMinutes = daysRangeCount * 24 * 60;
    const totalSnappingSlots = totalMinutes / snappingStepSize;
    const slotWidth = timelineFrameWidth / totalSnappingSlots;
    const startTime = this.store.selectSnapshot(
      ShiftTimelineDataState.timeframe,
    ).start;
    const timeDiffInMinutes =
      (new Date(time).getTime() - startTime) / (60 * 1000);

    const px = (timeDiffInMinutes / snappingStepSize) * slotWidth;

    if (skipShadowShiftAllowance) {
      return px;
    } else {
      return px - DragAndDropModeService.SHADOW_SHIFT_ALLOWANCE_PIXEL;
    }
  }

  dragItemDatesAndDisplayRange(
    itemLeftPosition: number,
    duration: number,
  ): {
    currentDemarcatorRange: {
      left: number;
      right: number;
      shiftDuration: number;
      adjustmentNeeded: boolean;
    };
    itemStartDate: Date;
    itemEndDate: Date;
  } {
    const timeframe = this.store.selectSnapshot(
      ShiftTimelineDataState.timeframe,
    );
    const frameLength = this.store.selectSnapshot(
      ShiftTimelineState.frameLength,
    );
    const itemStartDate = getDateFromPixelPosition({
      pixelPosition: itemLeftPosition,
      timeframe,
      frameLength: frameLength,
    });
    const itemEndDate = startOfMinute(addMilliseconds(itemStartDate, duration));
    const itemRightPosition = runInInjectionContext(
      this.environmentInjector,
      () => {
        const ganttDateToPositionPxPipe = inject(GanttDateToPositionPxPipe);
        return ganttDateToPositionPxPipe.transform(itemEndDate, {
          fromRightSide: true,
          timelineWidthPixel: this.store.selectSnapshot(
            ShiftTimelineState.frameLength,
          ),
          interval: this.store.selectSnapshot(ShiftTimelineDataState.timeframe),
        });
      },
    );
    const currentDemarcatorRange = {
      left: itemLeftPosition,
      right: itemRightPosition,
      shiftDuration: duration,
      adjustmentNeeded: true,
    };
    return { currentDemarcatorRange, itemStartDate, itemEndDate };
  }
}
